import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Package, User, LogOut, Settings, ShoppingBag, Building2 } from 'lucide-react';
import PhoneAuthModal from './PhoneAuthModal';
import { userAPI } from '../utils/api';

function Header() {
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      userAPI.getProfile()
        .then(setUser)
        .catch(() => {
          localStorage.removeItem('auth_token');
        });
    }

    // CF_Authorization 쿠키 확인
    const hasCFAuth = document.cookie.split(';').some(c => c.trim().startsWith('CF_Authorization='));
    setIsAdmin(hasCFAuth);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  const isActive = (path) => location.pathname === path;

  const handleCartClick = () => {
    if (user) {
      navigate('/cart');
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            {/* 로고 */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <img
                src="/logo.png"
                alt="레이어블샵"
                onClick={() => navigate('/')}
                className="h-8 cursor-pointer transition-opacity"
              />
                <h1 className="text-xl font-bold text-gray-400">Shop</h1>
              </div>
            
              {/* 네비게이션 */}
              <nav className="hidden md:flex items-center gap-1">
                <button
                  onClick={() => navigate('/products')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    isActive('/products')
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <ShoppingBag className="w-4 h-4" />
                  상품
                </button>
                <button
                  onClick={handleCartClick}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    isActive('/cart')
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <ShoppingCart className="w-4 h-4" />
                  장바구니
                </button>
                <button
                  onClick={() => navigate('/orders')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    isActive('/orders')
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Package className="w-4 h-4" />
                  주문내역
                </button>
                {isAdmin && (
                  <button
                    onClick={() => navigate('/admin')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      isActive('/admin')
                        ? 'bg-teal-50 text-teal-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Settings className="w-4 h-4" />
                    관리자
                  </button>
                )}
              </nav>
            </div>

            {/* 사용자 영역 */}
            <div>
              {user ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">{user.name || user.phone}</span>
                    {user.user_type === 'B2B' && (
                      <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                        <Building2 className="w-3 h-3" />
                        B2B
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">로그아웃</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="bg-teal-500 text-white px-5 py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors"
                >
                  로그인
                </button>
              )}
            </div>
          </div>

          {/* 모바일 네비게이션 */}
          <nav className="md:hidden flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 overflow-x-auto">
            <button
              onClick={() => navigate('/products')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                isActive('/products')
                  ? 'bg-teal-50 text-teal-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              상품
            </button>
            <button
              onClick={handleCartClick}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                isActive('/cart')
                  ? 'bg-teal-50 text-teal-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              장바구니
            </button>
            <button
              onClick={() => navigate('/orders')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                isActive('/orders')
                  ? 'bg-teal-50 text-teal-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Package className="w-3.5 h-3.5" />
              주문내역
            </button>
            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                  isActive('/admin')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Settings className="w-3.5 h-3.5" />
                관리자
              </button>
            )}
          </nav>
        </div>
      </header>

      <PhoneAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={(userData) => {
          setUser(userData);
          setShowAuthModal(false);
        }}
      />
    </>
  );
}

export default Header;
